import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { throwError, Observable, from, of, switchMap } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SubmitParams, GetDiligenceParams, PostDiligenceParams, PutDiligenceParams } from '../models/params';
import { AuthService } from '@auth0/auth0-angular'; // Import AuthService
import { CreateCreditDto, Credit, AdjustCreditDto } from '../models/credit';
import { Organization, CreateOrganizationDto, UpdateOrganizationDto } from '../models/organization';
import { ReportSpec, ReportSpecResponse } from '../interfaces/report-spec.interface';
import { AuthSessionService } from './auth-session.service';
import { Package } from '../models/package';
import { SelectedVesselBlockName, SelectedVesselDetailsResponse } from '../models/selected-vessel-data.model';
import { AviReportResponse, normalizeAviReport } from '../models/avi-report.model';

export interface DiligenceQueryParams {
  page: number;
  limit: number;
  sort: {
    field: string;
    order: 'asc' | 'desc';
  };
  filters: {
    phase?: number[];
    user_email?: string[];
    report?: string;
    entityType?: string;
    organization_id?: string;
    from_date?: string;
    to_date?: string;
  };
  search?: string;
}

interface DiligenceResponse {
  data: any[];
  metadata: {
    total: number;
    page: number;
    totalPages: number;
    limit: number;
  };
}

export interface PaymentCheckout {
  id: string;
  status: 'created' | 'approved' | 'declined' | 'requires3ds';
  reasonCode?: string | null;
  reason?: string | null;
  amountCents?: number;
  currency?: string;
  expiresAt?: string;
  transaction?: Record<string, unknown>;
}

export interface PaymentCheckoutInput {
  type?: 'one_time' | 'subscription_start' | 'payment_method_update';
  planCode?: string;
  companies?: string[];
  packages?: string[];
  uboSelected?: boolean;
}

export interface SubscriptionPlan {
  code: string;
  version: number;
  priceCents: number;
  currency: string;
  interval: { unit: string; count: number };
  firstChargePolicy: string;
  entitlement: Record<string, boolean>;
}

export interface SubscriptionBenefit {
  code: 'ucb' | 'avi';
  granted: number;
  consumed: number;
  remaining: number;
  validFrom?: string | null;
  validUntil?: string | null;
}

export interface Subscription {
  id: string;
  planCode: string;
  status: 'active' | 'past_due' | 'canceling' | 'canceled' | 'suspended' | 'pending';
  priceCents: number | null;
  currency: string | null;
  interval: { unit: string; count: number } | null;
  firstChargePolicy: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextRenewalAt: string | null;
  cancelAtPeriodEnd: boolean;
  willRenew: boolean;
  canCancel: boolean;
  canUpdatePaymentMethod: boolean;
  benefits?: SubscriptionBenefit[];
  entitlement: Record<string, boolean>;
}

export type QuickTourOutcome = 'completed' | 'skipped';

export interface OnboardingState {
  tutorial_required: boolean;
  tutorial_completed_at: string | null;
  tutorial_outcome: QuickTourOutcome | null;
  quick_tour_version?: number | null;
}

export interface MySubscriptionResponse {
  subscription: Subscription | null;
  onboarding?: OnboardingState;
  trialConsumed?: boolean;
  trialEligible?: boolean;
}

export interface PaymentAttemptInput {
  cardSut: string;
  cvvSut: string;
  expiry: string;
  billing: Record<string, string>;
}

export interface PaymentVerificationInput {
  x3dsActionCode: string;
  xCavv: string;
  xEci: string;
  xRefNum: string;
  x3dsAuthenticationStatus: string;
  x3dsSignatureVerificationStatus: string;
}

interface FilterOptionsResponse {
  users: string[];
  organizations: { id: string; name: string; }[];
}

export interface VesselSearchCandidate {
  imo: string | null;
  mmsi: string | null;
  uuid: string | null;
  name: string | null;
  type: string | null;
  countryIso: string | null;
  yearBuilt: string | null;
  latitude?: number | null;
  longitude?: number | null;
  speed?: number | null;
  course?: number | null;
  heading?: number | null;
  destination?: string | null;
  etaUtc?: string | null;
  lastPositionUtc?: string | null;
}

export interface VesselSearchResponse {
  data: VesselSearchCandidate[];
}

export type BillOfLadingTradeType = 'imp' | 'exp';
export type BillOfLadingCountry = { code: string | null; name: string | null } | string | null;

export interface BillOfLadingLookupRecord {
  id: string;
  billOfLadingNumber: string;
  recordDate: string | null;
  transportType: string | null;
  loadingPort: string | null;
  unloadingPort: string | null;
  countries: { import: BillOfLadingCountry; export: BillOfLadingCountry; origin: BillOfLadingCountry };
  shipper: { id: string | null; name: string | null };
  consignee: { id: string | null; name: string | null };
  cargo: { description: string | null; hsCode: string | null; hsCodeDescription: string | null };
  quantity: { value: number | null; unit: string | null };
  weight: { value: number | null; unit: string | null };
  amount: number | null;
  carrier: { code: string | null; name: string | null };
  vessel: { name: string | null; code: string | null; voyageNumber: string | null };
  containerNumber: string | null;
  sourceRecord: Record<string, unknown>;
}

export interface BillOfLadingLookupPreview {
  tradeType: BillOfLadingTradeType;
  providerTotal: number;
  previewToken?: string;
  record: BillOfLadingLookupRecord | null;
}

export interface BillOfLadingLookupData {
  billOfLadingNumber: string;
  previews: BillOfLadingLookupPreview[];
  totalProviderRecords: number;
  previewedRecords: number;
  fullExport: { required: boolean; providerRecordsNotPreviewed: number };
}

export interface BillOfLadingLookupResponse {
  data: BillOfLadingLookupData;
}

export interface BillOfLadingRoutePort {
  label: string;
  matchedName: string | null;
  longitude: number;
  latitude: number;
}

export interface BillOfLadingRoute {
  kind: 'bol_port_pair';
  coordinates: [number, number][];
  loadingPort: BillOfLadingRoutePort;
  unloadingPort: BillOfLadingRoutePort;
  disclaimer: string;
}

export interface BillOfLadingTrackedRecord {
  id: number;
  billOfLadingNumber: string;
  recordDate: string | null;
  transportType: string | null;
  loadingPort: string | null;
  unloadingPort: string | null;
  record: BillOfLadingLookupRecord | null;
  route: BillOfLadingRoute | null;
  mapState: { visible: boolean; pinned: boolean };
  createdAt?: string;
}

export interface BillOfLadingTrackedResponse {
  data: BillOfLadingTrackedRecord;
}

export interface BillOfLadingTrackedListResponse {
  data: BillOfLadingTrackedRecord[];
}

export interface BillOfLadingDatasetRequestResponse {
  requestId: string;
  status: 'accepted';
}

export interface VesselLiveObservation {
  imo: string | null;
  mmsi: string | null;
  uuid: string | null;
  name: string | null;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  course: number | null;
  heading: number | null;
  navigationStatus: string | null;
  destination: string | null;
  etaUtc: string | null;
  lastPositionUtc: string | null;
  source: string;
}

export interface VesselLiveResponse {
  data: VesselLiveObservation;
}

export interface AoiBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

export interface AreaVesselCandidate {
  imo: string | null;
  mmsi: string | null;
  uuid: string | null;
  name: string | null;
  type: string | null;
  countryIso: string | null;
  latitude: number | null;
  longitude: number | null;
  speed: number | null;
  course: number | null;
  heading: number | null;
  destination: string | null;
  etaUtc: string | null;
  lastPositionUtc: string | null;
  distanceNm: number | null;
}

export interface AoiSearchResponse {
  data: AreaVesselCandidate[];
}

export interface TrackedVesselRecord {
  imo: string;
  name: string;
  trackedAt: string;
}

export interface TrackedVesselsResponse {
  data: TrackedVesselRecord[];
}

export interface VesselAisHistoryPosition {
  latitude: number;
  longitude: number;
  observed_at: string;
  speed?: number | null;
  course?: number | null;
  heading?: number | null;
  destination?: string | null;
}

export interface VesselAisHistoryTrack {
  id: string;
  from: string;
  to: string;
  positions: VesselAisHistoryPosition[];
}

export interface VesselAisHistoryData {
  imo: string;
  name: string;
  available_from?: string;
  available_to?: string;
  coverage: 'requested_window' | 'partial';
  source?: string;
  cache_status?: 'fresh' | 'cached';
  tracks: VesselAisHistoryTrack[];
  position_count?: number;
  sampling?: { applied: boolean; method?: string };
  warning?: string;
}

export interface VesselAisHistoryResponse {
  status: 'queued' | 'running' | 'complete' | 'failed';
  request_id?: string;
  progress?: { provider_pages_fetched?: number; provider_pages_total?: number | null; positions_collected?: number };
  data?: VesselAisHistoryData;
  partial?: VesselAisHistoryData;
  error?: string | null;
  warning?: string;
}

export type MaritimeRequestPayload =
  | { kind: 'dark_ship'; aoi: AoiBounds }
  | { kind: 'product_request'; productCode: string; imo: string | null; vesselContext?: Partial<VesselSearchCandidate> & { query?: string } };

export interface MaritimeRequestResponse {
  requestId: string;
  status: 'accepted';
  payment?: { charged: number; remainingCredits: number; benefitCode?: 'ucb' | 'avi' };
}

export interface PaidManualRequestPayload {
  kind: 'product_request';
  productCode: string;
  imo: string | null;
  vesselContext?: Record<string, unknown>;
  paymentCheckoutId?: string;
  paymentDetails?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    public http: HttpClient,
    private auth: AuthService,
    private authSession: AuthSessionService
  ) { }

  private getAuthHeaders(): Observable<HttpHeaders> {
    return from(this.auth.getAccessTokenSilently()).pipe(
      mergeMap(token => {
        const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
        return of(headers);
      }),
      catchError(error => {
        this.authSession.handleSessionExpired();
        return throwError(() => error);
      })
    );
  }

  getCompanies(httParams: SubmitParams, page: number, pageSize: number) {
    const params = new HttpParams()
      .set('country', httParams.country)
      .set('query', httParams.query)
      .set('city', httParams.city as string)
      .set('page', page)
      .set('pageSize', pageSize)
      .set('province', httParams.province as string);

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}getCompanies`, { params: params, headers: headers }))
    );
  }

  getIndividuals(httParams: SubmitParams, page: number, pageSize: number) {
    const params = new HttpParams()
      .set('country', httParams.country)
      .set('query', httParams.query)
      .set('dateOfBirth', httParams.dateOfBirth as string)
      .set('pageSize', pageSize)
      .set('page', page);

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}getIndividuals`, { params: params, headers: headers }))
    );
  }

  searchVessels(query: string): Observable<VesselSearchResponse> {
    const params = new HttpParams().set('query', query.trim());

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<VesselSearchResponse>(`${environment.baseUrl}vessels/search`, { params, headers }))
    );
  }

  lookupBillOfLading(billOfLadingNumber: string): Observable<BillOfLadingLookupResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<BillOfLadingLookupResponse>(
        `${environment.baseUrl}bol/lookup`,
        { billOfLadingNumber: billOfLadingNumber.trim().toUpperCase() },
        { headers }
      ))
    );
  }

  resolveBillOfLadingRoute(previewToken: string): Observable<{ data: BillOfLadingRoute }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<{ data: BillOfLadingRoute }>(
        `${environment.baseUrl}bol/routes/preview`, { previewToken }, { headers }
      ))
    );
  }

  createTrackedBillOfLading(previewToken: string): Observable<BillOfLadingTrackedResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<BillOfLadingTrackedResponse>(
        `${environment.baseUrl}bol/tracked`, { previewToken }, { headers }
      ))
    );
  }

  getTrackedBillOfLadings(): Observable<BillOfLadingTrackedListResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<BillOfLadingTrackedListResponse>(`${environment.baseUrl}bol/tracked`, { headers }))
    );
  }

  deleteTrackedBillOfLading(trackedBolId: number): Observable<void> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.delete<void>(`${environment.baseUrl}bol/tracked/${encodeURIComponent(trackedBolId)}`, { headers }))
    );
  }

  updateTrackedBillOfLadingMapState(trackedBolId: number, mapState: { visible: boolean; pinned: boolean }): Observable<BillOfLadingTrackedResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.patch<BillOfLadingTrackedResponse>(
        `${environment.baseUrl}bol/tracked/${encodeURIComponent(trackedBolId)}/map-state`, mapState, { headers }
      ))
    );
  }

  resolveTrackedBillOfLadingRoute(trackedBolId: number): Observable<BillOfLadingTrackedResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<BillOfLadingTrackedResponse>(
        `${environment.baseUrl}bol/tracked/${encodeURIComponent(trackedBolId)}/route`, {}, { headers }
      ))
    );
  }

  requestBillOfLadingDataset(previewToken: string, trackedBolId: number | null, idempotencyKey: string): Observable<BillOfLadingDatasetRequestResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<BillOfLadingDatasetRequestResponse>(
        `${environment.baseUrl}bol/dataset-requests`,
        { previewToken, trackedBolId, requestType: 'related_shipments_or_broader_dataset' },
        { headers: headers.set('Idempotency-Key', idempotencyKey) }
      ))
    );
  }

  getPackages(): Observable<Package[]> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<Package[]>(`${environment.baseUrl}packages`, { headers }))
    );
  }

  getLiveVessel(imo: string): Observable<VesselLiveResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<VesselLiveResponse>(`${environment.baseUrl}vessels/${encodeURIComponent(imo)}/live`, { headers }))
    );
  }

  getSelectedVesselDetails(
    imo: string,
    include: SelectedVesselBlockName[] = [],
    aoiCenter?: { latitude: number; longitude: number }
  ): Observable<SelectedVesselDetailsResponse> {
    let params = new HttpParams();
    const blocks = [...new Set(include)];
    if (blocks.length) params = params.set('include', blocks.join(','));
    if (aoiCenter) {
      params = params
        .set('aoiLatitude', aoiCenter.latitude)
        .set('aoiLongitude', aoiCenter.longitude);
    }

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<SelectedVesselDetailsResponse>(
        `${environment.baseUrl}vessels/${encodeURIComponent(imo)}/details`,
        { headers, params }
      ))
    );
  }

  searchVesselsInArea(bounds: AoiBounds): Observable<AoiSearchResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<AoiSearchResponse>(`${environment.baseUrl}vessels/aoi-search`, bounds, { headers }))
    );
  }

  getTrackedVessels(): Observable<TrackedVesselsResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<TrackedVesselsResponse>(`${environment.baseUrl}vessels/tracked`, { headers }))
    );
  }

  trackVessel(vessel: Pick<TrackedVesselRecord, 'imo' | 'name'>): Observable<{ data: TrackedVesselRecord }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<{ data: TrackedVesselRecord }>(`${environment.baseUrl}vessels/tracked`, vessel, { headers }))
    );
  }

  deleteTrackedVessel(imo: string): Observable<void> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.delete<void>(`${environment.baseUrl}vessels/tracked/${encodeURIComponent(imo)}`, { headers }))
    );
  }

  startVesselAisHistory(imo: string): Observable<VesselAisHistoryResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<VesselAisHistoryResponse>(
        `${environment.baseUrl}vessels/${encodeURIComponent(imo)}/ais-history`,
        { scope: 'recent_window', lookback_months: 24 },
        { headers }
      ))
    );
  }

  getVesselAisHistory(imo: string, requestId: string): Observable<VesselAisHistoryResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<VesselAisHistoryResponse>(
        `${environment.baseUrl}vessels/${encodeURIComponent(imo)}/ais-history/${encodeURIComponent(requestId)}`,
        { headers }
      ))
    );
  }

  submitMaritimeRequest(payload: MaritimeRequestPayload): Observable<MaritimeRequestResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<MaritimeRequestResponse>(`${environment.baseUrl}maritime-requests`, payload, { headers }))
    );
  }

  submitPaidManualRequest(payload: PaidManualRequestPayload, idempotencyKey: string): Observable<MaritimeRequestResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<MaritimeRequestResponse>(`${environment.baseUrl}maritime-requests/paid`, payload, {
        headers: headers.set('Idempotency-Key', idempotencyKey)
      }))
    );
  }

  getPaymentCheckout(id: string): Observable<PaymentCheckout> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<PaymentCheckout>(`${environment.baseUrl}payments/checkouts/${encodeURIComponent(id)}`, { headers }))
    );
  }

  getDiligencesByPhase(queryParams: DiligenceQueryParams = {
    page: 1,
    limit: 20,
    sort: {
      field: 'updated_at',
      order: 'desc'
    },
    filters: {}
  }) {
    let params = new HttpParams();
    
    // Add pagination
    if (queryParams.page) params = params.set('page', queryParams.page.toString());
    if (queryParams.limit) params = params.set('limit', queryParams.limit.toString());
    
    // Add sorting
    if (queryParams.sort) {
      params = params.set('sort[field]', queryParams.sort.field);
      params = params.set('sort[order]', queryParams.sort.order);
    }
    
    // Add filters
    if (queryParams.filters) {
      const { phase, user_email, report, entityType, organization_id, from_date, to_date } = queryParams.filters;
      if (phase) {
        phase.forEach(p => {
          params = params.append('filters[phase][]', p.toString());
        });
      }
      if (user_email) {
        user_email.forEach(email => {
          params = params.append('filters[user_email][]', email);
        });
      }
      if (report) params = params.set('filters[report]', report);
      if (entityType) params = params.set('filters[entityType]', entityType);
      if (organization_id) params = params.set('filters[organization_id]', organization_id);
      if (from_date) params = params.set('filters[from_date]', from_date);
      if (to_date) params = params.set('filters[to_date]', to_date);
    }
    
    // Add search
    if (queryParams.search) params = params.set('search', queryParams.search);

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<DiligenceResponse>(`${environment.baseUrl}reports/query`, 
        { params, headers }))
    );
  }

  getDiligencesByEmail() {

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}reports/user`, { headers: headers }))
    );
  }

  getDiligenceById(id: string) {
    
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}reports/${id}`, { headers: headers }))
    );
  }

  getAviReport(uuid: string): Observable<AviReportResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<unknown>(`${environment.baseUrl}reports/${encodeURIComponent(uuid)}/avi`, { headers })),
      map(response => normalizeAviReport(response))
    );
  }

  postDiligence(httParams: PostDiligenceParams) {
    const body = {
      companies: httParams.companies,
      packages: httParams.packages,
      ubo: httParams.ubo,
      ...(httParams.paymentCheckoutId ? { paymentCheckoutId: httParams.paymentCheckoutId } : {})
    };

    return this.getAuthHeaders().pipe(
      mergeMap(headers => 
        this.http.post<any>(`${environment.baseUrl}reports`, body, { headers: headers }).pipe(
          catchError(error => {
            console.error('API error:', error);
            return throwError(error);
          })
        )
      )
    );
  }

  // ADMIN 

  putDiligence(httParams: PutDiligenceParams) {
    const params = new HttpParams().set('uuid', httParams.uuid);

    const body = {
      "phase": httParams.phase,
      "repScore": httParams.repScore,
      "repSumm": httParams.repSumm,
      "critIssueSummary": httParams.critIssueSummary,
      "previousDiligencePhase": httParams.previousDiligencePhase,
      "critIssueDetails": httParams.critIssueDetails,
      "sancIssueSummary": httParams.sancIssueSummary,
      "email": httParams.userEmail
    };

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.put<any>(`${environment.baseUrl}reports/${httParams.uuid}`, body, { params: params, headers: headers }))
    );
  }

  updateReportStatus(reportId: string, action: 'void' | 'unvoid') {
    const body = { action };

    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<any>(`${environment.baseUrl}reports/${reportId}/status`, body, { headers }))
    );
  }

  getUsers() {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}users`, { headers: headers }))
    );
  }

  getUsersReport() {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}reports/users`, { headers: headers }))
    );
  }

  createPaymentCheckout(input: PaymentCheckoutInput): Observable<PaymentCheckout> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<PaymentCheckout>(`${environment.baseUrl}payments/checkouts`, input, { headers }))
    );
  }

  getSubscriptionPlans(): Observable<{ plans: SubscriptionPlan[] }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<{ plans: SubscriptionPlan[] }>(`${environment.baseUrl}payments/plans`, { headers }))
    );
  }

  getMySubscription(): Observable<MySubscriptionResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<MySubscriptionResponse>(`${environment.baseUrl}payments/subscriptions/me`, { headers }))
    );
  }

  cancelSubscription(id: string): Observable<{ subscription: Subscription }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<{ subscription: Subscription }>(`${environment.baseUrl}payments/subscriptions/${encodeURIComponent(id)}/cancel`, {}, { headers }))
    );
  }

  completeQuickTour(outcome: QuickTourOutcome): Observable<{ onboarding: OnboardingState }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<{ onboarding: OnboardingState }>(`${environment.baseUrl}me/onboarding/quick-tour/complete`, { outcome }, { headers }))
    );
  }

  attemptPaymentCheckout(id: string, input: PaymentAttemptInput): Observable<PaymentCheckout> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<PaymentCheckout>(`${environment.baseUrl}payments/checkouts/${encodeURIComponent(id)}/attempt`, input, { headers }))
    );
  }

  verifyPaymentCheckout3ds(id: string, input: PaymentVerificationInput): Observable<PaymentCheckout> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<PaymentCheckout>(`${environment.baseUrl}payments/checkouts/${encodeURIComponent(id)}/3ds/verify`, input, { headers }))
    );
  }

  listCredits(): Observable<Credit[]> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<Credit[]>(`${environment.baseUrl}credits`, { headers }))
    );
  }

  listCreditsForEntity(entityType: string, entityId: string) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<[Credit]>(`${environment.baseUrl}credits/${entityType}/${entityId}`, { headers: headers }))
    );
  }

  addCredit(credit: CreateCreditDto) {
    const body = {
      amount: credit.amount,
      entityType: credit.entityType,
      entityId: credit.entityId,
      startDate: credit.startDate,
      endDate: credit.endDate
    };
    
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<any>(`${environment.baseUrl}credits/add`, body, { headers: headers }))
    );
  }


  adjustCredit(adjustment: AdjustCreditDto) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<any>(
        `${environment.baseUrl}credits/${adjustment.id}/adjust`, 
        { amount: adjustment.amount },
        { headers: headers }
      ))
    );
  }

  getOrganizations() {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<any>(`${environment.baseUrl}organizations`, { headers: headers }))
    );
  }

  // Organization CRUD operations
  getOrganizationById(id: string) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<Organization>(`${environment.baseUrl}organizations/${id}`, { headers }))
    );
  }

  createOrganization(data: CreateOrganizationDto) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<Organization>(`${environment.baseUrl}organizations`, data, { headers }))
    );
  }

  updateOrganization(id: string, data: UpdateOrganizationDto) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.put<Organization>(`${environment.baseUrl}organizations/${id}`, data, { headers }))
    );
  }

  deleteOrganization(id: string) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.delete<void>(`${environment.baseUrl}organizations/${id}`, { headers }))
    );
  }

  addOrganizationMember(orgId: string, email: string) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<void>(`${environment.baseUrl}organizations/${orgId}/members`, { email }, { headers }))
    );
  }

  removeOrganizationMember(orgId: string, email: string) {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.delete<void>(`${environment.baseUrl}organizations/${orgId}/members/${email}`, { headers }))
    );
  }

  getFilterOptions(): Observable<FilterOptionsResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<FilterOptionsResponse>(`${environment.baseUrl}reports/filter-options`, { headers }))
    );
  }

  // Report Spec API Methods

  getReportSpec(uuid: string): Observable<ReportSpecResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.get<ReportSpecResponse>(`${environment.baseUrl}reports/${uuid}/spec`, { headers }))
    );
  }

  updateReportSpec(uuid: string, spec: ReportSpec): Observable<{ success: boolean }> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.put<{ success: boolean }>(`${environment.baseUrl}reports/${uuid}/spec`, { spec }, { headers }))
    );
  }

  regenerateReportSpec(uuid: string): Observable<ReportSpecResponse> {
    return this.getAuthHeaders().pipe(
      mergeMap(headers => this.http.post<ReportSpecResponse>(`${environment.baseUrl}reports/${uuid}/spec/regenerate`, {}, { headers }))
    );
  }

}
